import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Typography } from "@mui/material";
import { Chip, TextField } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import { formatCashFull } from "utils/formatCash";
import { PaymentLinkAmountMode } from "enums/PaymentLink";
import { PublicPaymentLink } from "hooks/usePayLinkSession";

interface PayLinkAmountSectionProps {
  paymentLink: PublicPaymentLink;
  amount: number | null;
  onAmountChange: (amount: number | null) => void;
  disabled?: boolean;
}

const PayLinkAmountSection: React.FC<PayLinkAmountSectionProps> = ({
  paymentLink,
  amount,
  onAmountChange,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const presets = paymentLink.presetAmounts || [];
  // An amount no preset matches can only have been typed, so it reopens as Other.
  const [other, setOther] = useState(
    amount != null && !presets.includes(amount),
  );

  const isPayerChooses =
    paymentLink.amountMode === PaymentLinkAmountMode.PAYER_CHOOSES;

  if (!isPayerChooses) {
    return (
      <Typography variant="stat">
        {formatCashFull(paymentLink.amount || 0, paymentLink.currency)}
      </Typography>
    );
  }

  const field = (
    <TextField
      type="number"
      label={presets.length ? t("Other amount") : t("Amount")}
      disabled={disabled}
      autoFocus={presets.length > 0}
      value={amount ?? ""}
      inputProps={{
        min: paymentLink.minAmount || 0,
        max: paymentLink.maxAmount || undefined,
        step: "0.01",
      }}
      onChange={(event) =>
        onAmountChange(
          event.target.value === "" ? null : Number(event.target.value),
        )
      }
      helperText={rangeHelper(paymentLink, t)}
    />
  );

  if (!presets.length) {
    return field;
  }

  // The presets and Other are one choice, as the tip's are, so the field only opens for Other.
  return (
    <Stack sx={{ gap: "var(--space-2)" }}>
      <Typography variant="body2">{t("Amount")}</Typography>
      <Inline gap="var(--space-2)">
        {presets.map((preset) => {
          const chosen = !other && amount === preset;

          return (
            <Chip
              key={preset}
              tone={chosen ? "brand" : "default"}
              aria-pressed={chosen}
              disabled={disabled}
              onClick={() => {
                setOther(false);
                onAmountChange(preset);
              }}
            >
              {formatCashFull(preset, paymentLink.currency)}
            </Chip>
          );
        })}
        <Chip
          tone={other ? "brand" : "default"}
          aria-pressed={other}
          disabled={disabled}
          onClick={() => {
            setOther(true);
            onAmountChange(null);
          }}
        >
          {t("Other")}
        </Chip>
      </Inline>
      {other && field}
    </Stack>
  );
};

function rangeHelper(
  paymentLink: PublicPaymentLink,
  t: (key: string, options?: Record<string, string>) => string,
): string {
  const { minAmount, maxAmount, currency } = paymentLink;
  const hasMin = minAmount != null;
  const hasMax = maxAmount != null;

  if (hasMin && hasMax) {
    return t("payLink.AmountRange", {
      min: formatCashFull(minAmount, currency),
      max: formatCashFull(maxAmount, currency),
    });
  }
  if (hasMin) {
    return t("payLink.AmountMinimum", {
      min: formatCashFull(minAmount, currency),
    });
  }
  if (hasMax) {
    return t("payLink.AmountMaximum", {
      max: formatCashFull(maxAmount, currency),
    });
  }

  return "";
}

export default PayLinkAmountSection;
